import { Component, Inject, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { OrganisationDataModelNode } from '@services/organisation-data.service';
import { DatapointEnergyMetric, SummaryReport } from '@services/report-data.service';
import { ListViewComponent } from '@syncfusion/ej2-angular-lists';
import { concat, Subject } from 'rxjs';

export class CreateOrganisationReportData {

	/** DataModel */
	public DataModel: OrganisationDataModelNode[];

	/** DPEnergyMetrics */
	public DPEnergyMetrics: DatapointEnergyMetric[];

	/** ExistingReport */
	public ExistingReport?: SummaryReport;
}

@Component({
	selector: 'app-create-organisation-report',
	templateUrl: './create-organisation-report.component.html',
	styleUrl: './create-organisation-report.component.scss'
})
export class CreateOrganisationReportComponent {

	/** Form Group */
	dialogForm: FormGroup;

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** organisationDataModel */
	public organisationDataModel: OrganisationDataModelNode[] = [];

	/** flattenedDataModel */
	public flattenedDataModel: OrganisationDataModelNode[] = [];

	/** fields */
	public fields: Object;

	/** includedNodes */
	public includedNodes: OrganisationDataModelNode[] = [];

	/** dataModelNodeFields */
	public dataModelNodeFields: Object = { text: "Name", id: "DataModelNodeID" };

	/** listViewInstance */
	@ViewChild('listviewInstance') listViewInstance!: ListViewComponent;

	/** reportDatapointEnergyMetrics */
	public reportDatapointEnergyMetrics: DatapointEnergyMetric[] = [];

	/** dataPointEnergyMetrics */
	public datapointEnergyMetrics: DatapointEnergyMetric[] = [];

	/** intervals */
	public intervals: { text: string, value: number }[] = [
		{ text: "24 Hours", value: 1 },
		{ text: "7 Days", value: 2 },
		{ text: "14 Days", value: 3 },
		{ text: "30 Days", value: 4 },
		{ text: "60 Days", value: 5 },
		{ text: "90 Days", value: 6 },
	];

	/** intervalFields */
	public intervalFields: Object = { text: "text", value: "value" };

	/** fileTypes */
	public fileTypes: { text: string, value: number }[] = [
		{ text: "PDF File (.pdf)", value: 1 },
		{ text: "Excel File (.xlsx)", value: 2 },
	];

	/** fileTypeFields */
	public fileTypeFields: Object = { text: "text", value: "value" };

	/** existingReport */
	public existingReport?: SummaryReport;

	/**
	 * Default Constructor
	 * @param fb fb
	 * @param dialogRef Referense to the Dialog box
	 * @param dialogData object interface
	 * @param authenticationService 
	 * @param globalFunctions globalFunctions
	 */
	constructor(
		private fb: FormBuilder,
		public dialogRef: MatDialogRef<CreateOrganisationReportComponent>,
		@Inject(MAT_DIALOG_DATA) public dialogData: CreateOrganisationReportData,
		private globalFunctions: GlobalFunctions
	) {
		if (dialogData.DataModel && dialogData.DPEnergyMetrics) {
			this.organisationDataModel = dialogData.DataModel;
			this.datapointEnergyMetrics = dialogData.DPEnergyMetrics;

			this.flattenedDataModel = this.flattenNodes(this.organisationDataModel, true);

			if (dialogData.ExistingReport) {
				this.existingReport = dialogData.ExistingReport;
				const folderNodeUIDs: string[] = this.existingReport?.FolderNodes.split(",");
				const folderNodes = this.flattenedDataModel.filter((x: OrganisationDataModelNode) => folderNodeUIDs.includes(x.DataModelNodeUID));
				const sensorNodeUIDs: string[] = this.existingReport?.SensorNodes.split(",");
				const sensorNodes = this.flattenedDataModel.filter((x: OrganisationDataModelNode) => sensorNodeUIDs.includes(x.DataModelNodeUID));
				const datapointNodeIDs: string[] = this.existingReport?.DatapointNodes.split(",");
				const datapointNodes = this.flattenedDataModel.filter((x: OrganisationDataModelNode) => datapointNodeIDs.includes(x.DataModelNodeUID));
				const _folderUIDs = folderNodes.map((x: OrganisationDataModelNode) => x.DataModelNodeUID);
				const _sensorUIDs = sensorNodes.map((x: OrganisationDataModelNode) => x.DataModelNodeUID);
				const _datapointUIDs = datapointNodes.map((x: OrganisationDataModelNode) => x.DataModelNodeUID);
				this.includedNodes = [
					...folderNodes.filter((node: OrganisationDataModelNode, index: number) => this.checkUniqueNode(_folderUIDs, node.DataModelNodeUID, index)),
					...sensorNodes.filter((node: OrganisationDataModelNode, index: number) => this.checkUniqueNode(_sensorUIDs, node.DataModelNodeUID, index)),
					...datapointNodes.filter((node: OrganisationDataModelNode, index: number) => this.checkUniqueNode(_datapointUIDs, node.DataModelNodeUID, index))
				];
			}

			this.fields = {
				dataSource: this.organisationDataModel,
				id: 'DataModelNodeUID',
				text: 'Name',
				child: 'SubChild',
				// iconCss: 'LocationType',
				// imageUrl: 'ImageURL',
				expanded: "Expanded",
				// hidden: 'Hidden',
				nodeType: "NodeType",
				parentUID: 'ParentNodeUID'
			};
		}
	}

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {
		if (!this.existingReport) {
			this.dialogForm = this.fb.group({
				name: ["", Validators.required],
				description: [""],
				interval: [1, Validators.required],
				fileType: [1, Validators.required],
				includedNodes: [[], Validators.required]
			});
		} else {
			this.dialogForm = this.fb.group({
				name: [this.existingReport.Name, Validators.required],
				description: [this.existingReport.Description ?? ""],
				interval: [this.existingReport.Interval, Validators.required],
				fileType: [this.existingReport.FileType, Validators.required],
				includedNodes: [this.includedNodes, Validators.required]
			});
		}
	}

	/**
	 * Cancel button for dialog
	 */
	onCancelClick(): void {
		if (this.dialogForm.dirty) {
			this.globalFunctions.UserConfirm$('Lose any unsaved changes?').subscribe(OKResult => {
				if (OKResult) { this.dialogRef.close(); }
				// do nothing if result is false
			});
		} else {
			this.dialogRef.close();
		}
	}

	/**
	 * respondToDrag
	 * @param event event  
	 */
	respondToDrag(event: any) {
		let elements: any[] = [];
		let target: HTMLElement | null = event.target;
		while (target) {
			elements.push(target);
			target = target.parentElement;
		}
		let targetElement: HTMLElement = elements.find((x: HTMLElement) => x.classList.contains("drag-target"));
		if (targetElement) {
			const draggedNode: any = this.flattenedDataModel.find((x: OrganisationDataModelNode) => x.DataModelNodeUID == event.draggedNodeData["id"]);
			if (draggedNode) {
				this.includedNodes.push(draggedNode);
				if (["folder", "organisation"].includes(draggedNode.NodeType)) {
					// add all subchildren 
					this.includedNodes.push(...draggedNode.SubChild);
					// get data for all subchildren
					[draggedNode, ...draggedNode.SubChild].forEach((node: any) => {
						if (node.NodeType != "datapoint") {
							const dpNodes = this.flattenNodes([node], true).filter((x: any) => x.DataPointID);
							const relevantMetrics: DatapointEnergyMetric[] = [];
							dpNodes.forEach((dpNode: any) => {
								const findMetric = this.datapointEnergyMetrics.find((x: DatapointEnergyMetric) => x.DatapointID == dpNode.DataPointID);
								if (findMetric) {
									relevantMetrics.push(findMetric);
									if (node.NodeType == "sensor") {
										this.reportDatapointEnergyMetrics.push(findMetric);
									}
								}
							});
							// this.reportTotalisedEnergyMetrics.push(this.getTotaliseEnergyMetric(node, dpNodes, relevantMetrics));
						} else {
							const findMetric = this.datapointEnergyMetrics.find((x: DatapointEnergyMetric) => x.DatapointID == draggedNode.DataPointID);
							if (findMetric) {
								this.reportDatapointEnergyMetrics.push(findMetric);
							}
						}
					});
				}
				else if (draggedNode.NodeType == "sensor") {
					// add all datapoints
					// get data for all datapoints 
					const relevantMetrics: DatapointEnergyMetric[] = [];
					draggedNode.Datapoints.forEach((dp: any) => {
						const findMetric = this.datapointEnergyMetrics.find((x: DatapointEnergyMetric) => x.DatapointID == dp.DataPointID);
						if (findMetric) {
							relevantMetrics.push(findMetric);
							this.reportDatapointEnergyMetrics.push(findMetric);
						}
						// this.reportTotalisedEnergyMetrics.push(this.getTotaliseEnergyMetric(event.draggedNode, event.draggedNode.Datapoints, relevantMetrics));
					});
				}
				else if (draggedNode.NodeType == "datapoint") {
					// add to report
					// get data
					const findMetric = this.datapointEnergyMetrics.find((x: DatapointEnergyMetric) => x.DatapointID == draggedNode.DataPointID);
					if (findMetric) {
						this.reportDatapointEnergyMetrics.push(findMetric);
					}
				}
				this.dialogForm.get("includedNodes")?.setValue(this.includedNodes);
				this.dialogForm.markAsDirty();
				this.dialogForm.updateValueAndValidity();
				if (this.listViewInstance) {
					this.listViewInstance.dataBind();
					this.listViewInstance.refresh();
				}
			}
		}
	}

	/**
	 * getNodeChildrenText
	 * @param OrganisationDataModelNode OrganisationDataModelNode 
	 */
	getNodeChildrenText(node: OrganisationDataModelNode): string {
		let result: string = "";
		let count: number | undefined = 0;
		switch (node.NodeType) {
			case "sensor":
				count = node.Datapoints?.length;
				result = `${count} DataPoint${count != 1 ? 's' : ''}`;
				break;
			case "organisation":
				count = node.SubChild?.length;
				result = `${count} ${count != 1 ? 'Children' : 'Child'}`;
				break;
			case "folder":
				count = node.SubChild?.length;
				result = `${count} ${count != 1 ? 'Children' : 'Child'}`;
				break;
			case "datapoint":
				break;
			default:
				break;
		}
		return result;
	}

	/**
	 * removeReportItemNode
	 * @param node node 
	 */
	removeReportItemNode(node: any) {
		if (node.DataPointID) {
			this.includedNodes = this.includedNodes.filter((x: any) => (x.DataPointID && x.DataPointID != node.DataPointID) || !x.DataPointID);
			this.reportDatapointEnergyMetrics = this.reportDatapointEnergyMetrics.filter((x: DatapointEnergyMetric) => x.DatapointID != node.DataPointID);
		} else {
			this.includedNodes = this.includedNodes.filter((x: any) => (x.DataModelNodeID && x.DataModelNodeID != node.DataModelNodeID) || !x.DataModelNodeID);
			const flattenedNodes = this.flattenNodes([node], true);
			flattenedNodes.forEach((_node: any) => {
				if (_node.DataPointID) {
					const findMetric = this.datapointEnergyMetrics.find((x: DatapointEnergyMetric) => x.DatapointID == _node.DataPointID);
					if (findMetric) {
						this.reportDatapointEnergyMetrics = this.reportDatapointEnergyMetrics.filter((x: DatapointEnergyMetric) => x.DatapointID != _node.DataPointID);
					}
				}
			});
		}
		this.dialogForm.get("includedNodes")?.setValue(this.includedNodes);
		this.dialogForm.markAsDirty();
		this.dialogForm.updateValueAndValidity();
		if (this.listViewInstance) {
			this.listViewInstance.dataBind();
			this.listViewInstance.refresh();
		}
	}

	/**
	 * flattenNodes
	 * @param nodes nodes  
	 * @returns 
	 */
	flattenNodes(nodes: OrganisationDataModelNode[], includeTopLevelNodes: boolean = false): OrganisationDataModelNode[] {
		let result: OrganisationDataModelNode[] = [];

		// Check if nodes is a valid array before proceeding
		if (!Array.isArray(nodes)) {
			return result;
		}

		if (includeTopLevelNodes) {
			result.push(...nodes);
		}

		// Iterate over each item in the input hierarchy
		for (const item of nodes) {
			if (item.SubChild && item.SubChild.length > 0) {
				result = result.concat(item.SubChild);
				result = result.concat(this.flattenNodes(item.SubChild));
			}
		}

		return result;
	}

	/**
	 * checkUniqueNode
	 * @param nodes nodes 
	 * @param checkingNode checkingNode  
	 * @param checkingIndex checkingIndex 
	 */
	checkUniqueNode(nodeUIDs: string[], checkingNodeUID: string, checkingIndex: number): boolean {
		return nodeUIDs.indexOf(checkingNodeUID) === checkingIndex;
	}
}
